import { useMemo, useState } from 'react'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  Button,
  DetailHero,
  Drawer,
  DrawerTabPanel,
  DrawerTabs,
  KeyValueGrid,
  MoneyValue,
  NavigationLink,
  MoneyReconciliation,
  StatusBadge,
  TechnicalDetails,
} from '@shared/design-system/ui'
import { toast } from '@shared/lib/toast/toast-store'
import { getUserFacingError } from '@shared/lib/errors/user-facing-error'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { paymentMethodLabel } from '@entities/sale'
import { formatMoney } from '@shared/lib/format/currency'
import { formatPercentageLabel } from '@shared/lib/format/percent'
import {
  requestAdminSaleRefund,
  useAdminReceivablesBySale,
  useAdminSale,
  useAdminSaleJourney,
  useAdminUsersLookup,
} from '../api/admin.api'
import type { SaleCoupon } from '../api/admin.api'
import { sumLedgerByCurrency as sumByCurrency } from '../lib/admin-money'
import type { MoneySummary } from '../lib/admin-money'
import { adminSaleLineKindLabel, adminSaleStatusPresentation } from '../lib/admin-sale-events'
import {
  vendepayFeeBasisLabel,
  vendepayFeeComponents,
  type VendepayFeeComponent,
} from '../lib/vendepay-fee-presentation'
import {
  adminChargeLabel,
  adminProductLabel,
  adminSaleLineOfferLabel,
  adminSaleLineProductLabel,
} from '../lib/admin-sale-charge'
import {
  adminSaleJourneyId,
  buildAdminSaleRefundTargets,
  type AdminSaleRefundTarget,
} from '../lib/admin-sale-refund'
import {
  buildLedgerOwnerDisplayGroups,
  buildParticipantDisplaySummaries,
  isCurrencyFeeLedgerEntry,
  isVendepaySaleFeeLedgerEntry,
  ledgerEntryDisplayHint,
  ledgerEntryDisplayLabel,
  sumParticipantDisplayTotals,
} from '../lib/admin-sale-detail-money'
import { AdminProviderChargeResult } from './AdminProviderChargeResult'
import { AdminSaleFinancialSummary } from './AdminSaleFinancialSummary'
import { AdminSaleRefundDialog } from './AdminSaleRefundDialog'
import { adminUserDisplay } from './admin-user-display'
import {
  muted,
  saleChargeTag,
  saleCompositionTotalLabel,
  saleCompositionTotalRow,
  saleDetail,
  saleDetailOffer,
  saleDivider,
  saleEntryAmount,
  saleEntryBody,
  saleEntryMeta,
  saleItemAside,
  saleLine,
  saleLineHint,
  saleLineLabel,
  saleLineValue,
  saleNetLabel,
  saleNetLead,
  saleNetNote,
  saleNetRow,
  saleNetValue,
  salePlatformLabel,
  salePlatformNote,
  salePlatformRow,
  salePlatformValue,
  saleSection,
  saleSectionTitle,
  saleSplitLead,
  saleSplitMeta,
  saleSplitName,
  saleSplitRow,
  saleStatementEntry,
  saleStatementGroup,
  saleStatementHeader,
  saleStatementRows,
  saleStatementTitle,
  saleStatusPills,
  saleUserIdentity,
  saleUserName,
  splitGrid,
} from './admin.css'

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function MoneySummaryList({ rows, empty }: { rows: MoneySummary[]; empty: string }) {
  if (rows.length === 0) {
    return <span className={muted}>{empty}</span>
  }
  return (
    <>
      {rows.map((row) => (
        <MoneyValue amount={row.amount} currency={row.currency} key={row.currency} size="sm" />
      ))}
    </>
  )
}

function statusTone(status: string) {
  if (status === 'PENDING' || status === 'PROCESSING' || status === 'PROCESSING_PARTIAL') {
    return 'awaiting'
  }
  if (status === 'REVOKED' || status === 'FAILED') return 'closed'
  if (status.includes('DEBT_SETTLED')) return 'informative'
  if (status.includes('SETTLED') || status === 'PAID') return 'success'
  return 'neutral'
}

function ledgerStatusLabel(status: string) {
  const labels: Record<string, string> = {
    PENDING: 'Pendente no ledger',
    PROCESSING: 'Processando',
    PROCESSING_PARTIAL: 'Parcial',
    PAID: 'Pago',
    REVOKED: 'Revogado',
    FAILED: 'Falhou',
    DEBT_SETTLED: 'Abatido da dívida',
    DEBT_SETTLED_PARTIAL: 'Abatido parcial',
  }

  if (labels[status]) {
    return labels[status]
  }

  return status
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function feeComponentLabel(component: VendepayFeeComponent): string {
  if (component.type === 'FIXED') return 'Taxa fixa'
  if (component.type === 'PERCENTAGE') return 'Taxa percentual'
  return 'Taxa Vendepay'
}

function feeComponentMeta(component: VendepayFeeComponent): string {
  const parts = [
    component.kind ? adminSaleLineKindLabel(component.kind) : null,
    component.percentage && component.baseAmount
      ? `${component.percentage}% sobre ${component.baseAmount.currency} ${component.baseAmount.amount}`
      : null,
    component.inferred ? 'componente recuperado do histórico' : null,
  ].filter(Boolean)
  return parts.join(' · ')
}

function couponDiscountLabel(coupon: SaleCoupon): string {
  if (coupon.type === 'percent') {
    return coupon.percent
      ? formatPercentageLabel(coupon.percent)
      : formatMoney(coupon.discountAmount.amount, coupon.discountAmount.currency)
  }

  return coupon.configuredAmount
    ? formatMoney(coupon.configuredAmount.amount, coupon.configuredAmount.currency)
    : formatMoney(coupon.discountAmount.amount, coupon.discountAmount.currency)
}

function couponSummaryLabel(coupon: SaleCoupon): string {
  return `${coupon.code} · ${couponDiscountLabel(coupon)}`
}

function CouponDetails({ coupon }: { coupon: SaleCoupon }) {
  return (
    <div className={saleSection}>
      <span className={saleSectionTitle}>Cupom aplicado</span>
      <div className={saleLine}>
        <span className={saleLineLabel}>Código</span>
        <span className={saleLineValue}>{coupon.code}</span>
      </div>
      <div className={saleLine}>
        <span className={saleLineLabel}>Desconto</span>
        <span className={saleLineValue}>{couponDiscountLabel(coupon)}</span>
      </div>
      <div className={saleLine}>
        <span className={saleLineLabel}>Valor original</span>
        <span className={saleLineValue}>
          <MoneyValue
            amount={coupon.amountBeforeDiscount.amount}
            currency={coupon.amountBeforeDiscount.currency}
            size="sm"
          />
        </span>
      </div>
      <div className={saleLine}>
        <span className={saleLineLabel}>Desconto do comprador</span>
        <span className={saleLineValue}>
          <MoneyValue
            amount={coupon.discountAmount.amount}
            currency={coupon.discountAmount.currency}
            size="sm"
          />
        </span>
      </div>
      {coupon.settlement ? (
        <>
          <div className={saleLine}>
            <span className={saleLineLabel}>Liquidação original</span>
            <span className={saleLineValue}>
              <MoneyValue
                amount={coupon.settlement.amountBeforeDiscount.amount}
                currency={coupon.settlement.amountBeforeDiscount.currency}
                size="sm"
              />
            </span>
          </div>
          <div className={saleLine}>
            <span className={saleLineLabel}>Desconto na liquidação</span>
            <span className={saleLineValue}>
              <MoneyValue
                amount={coupon.settlement.discountAmount.amount}
                currency={coupon.settlement.discountAmount.currency}
                size="sm"
              />
            </span>
          </div>
        </>
      ) : null}
    </div>
  )
}

type AdminSaleDetailDrawerProps = {
  saleId: string | null
  onClose: () => void
}

export function AdminSaleDetailDrawer({ saleId, onClose }: AdminSaleDetailDrawerProps) {
  const customServiceFees = useFeatureFlag('customServiceFees')
  const [refundReason, setRefundReason] = useState('')
  const [refundTarget, setRefundTarget] = useState<AdminSaleRefundTarget | null>(null)
  const [activeTab, setActiveTab] = useState('summary')
  const queryClient = useQueryClient()
  const currentUser = useSession((state) => state.user)
  const canRefund = hasAdminPermission(currentUser, 'SALE_REFUND')
  const detail = useAdminSale(saleId)
  const saleReceivables = useAdminReceivablesBySale(saleId)
  const sale = detail.data?.sale ?? null
  const journeyId = sale ? adminSaleJourneyId(sale) : null
  const journey = useAdminSaleJourney(journeyId)
  const refundTargets = useMemo(
    () => (sale ? buildAdminSaleRefundTargets(sale, journey.data ?? []) : []),
    [journey.data, sale],
  )
  const refundableTargets = refundTargets.filter((target) => target.canRequestRefund)
  const salePresentation = sale ? adminSaleStatusPresentation(sale) : null
  const buyerAmountLabel =
    sale?.status === 'FAILED'
      ? 'Valor tentado'
      : sale?.status === 'PENDING'
        ? 'Aguardando pagamento'
        : 'Pago pelo comprador'
  const ledgerEntries = useMemo(
    () => saleReceivables.data?.items ?? [],
    [saleReceivables.data?.items],
  )
  const split = useMemo(
    () => (sale ? buildParticipantDisplaySummaries(ledgerEntries, sale) : []),
    [ledgerEntries, sale],
  )
  const ledgerGroups = useMemo(
    () => (sale ? buildLedgerOwnerDisplayGroups(ledgerEntries, sale) : []),
    [ledgerEntries, sale],
  )
  const participantIds = useMemo(() => {
    const ids = new Set<string>()
    if (sale?.producerId) ids.add(sale.producerId)
    if (sale?.affiliateId) ids.add(sale.affiliateId)
    if (sale?.coProducerId) ids.add(sale.coProducerId)
    if (sale?.seedId) ids.add(sale.seedId)
    for (const part of split) {
      ids.add(part.recipientId)
    }
    for (const entry of ledgerEntries) {
      if (entry.recipientId && entry.type !== 'VENDEPAY') {
        ids.add(entry.recipientId)
      }
    }
    return [...ids]
  }, [ledgerEntries, sale, split])
  const participantLookup = useAdminUsersLookup(participantIds)
  const producerDisplay = sale?.producerId
    ? adminUserDisplay({
        userId: sale.producerId,
        usersById: participantLookup.usersById,
        isLoading: participantLookup.isLoading,
      })
    : null
  const participantTotals = sumParticipantDisplayTotals(split)
  const vendepayFees = ledgerEntries.filter(isVendepaySaleFeeLedgerEntry)
  const vendepayFeeBasis = vendepayFeeBasisLabel(vendepayFees)
  const vendepayFeeBreakdown = vendepayFeeComponents(vendepayFees)
  const currencyFees = ledgerEntries.filter(isCurrencyFeeLedgerEntry)
  const buyerConversion = ledgerEntries.filter((entry) => entry.role === 'buyer-fx')
  const otherVendepayFees = ledgerEntries.filter(
    (entry) =>
      entry.type === 'VENDEPAY' &&
      !vendepayFees.includes(entry) &&
      !currencyFees.includes(entry) &&
      !buyerConversion.includes(entry),
  )
  const refundMutation = useMutation({
    mutationFn: (currentSaleId: string) =>
      requestAdminSaleRefund(currentSaleId, {
        ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
      }),
    onSuccess: (_refundedSale, refundedSaleId) => {
      toast.success('Reembolso solicitado e enviado para processamento.')
      setRefundTarget(null)
      setRefundReason('')
      void queryClient.invalidateQueries({ queryKey: ['admin-sale', saleId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-sale', refundedSaleId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-sale-journey', journeyId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-sales-infinite'] })
    },
    onError: (error) => {
      toast.error(
        getUserFacingError(error, {
          title: 'Não foi possível solicitar o reembolso',
          fallback: 'Confira o estado da venda e tente novamente.',
        }),
      )
    },
  })

  function closeDrawer() {
    setActiveTab('summary')
    setRefundTarget(null)
    setRefundReason('')
    onClose()
  }

  function openRefund(target: AdminSaleRefundTarget) {
    setRefundReason('')
    setRefundTarget(target)
  }

  const tabs = [
    { id: 'summary', label: 'Resumo' },
    { id: 'composition', label: 'Composição' },
    { id: 'ledger', label: 'Extrato' },
    { id: 'technical', label: 'Técnico' },
  ]

  const technicalRows = sale
    ? [
        { label: 'Cobrança', value: sale.id },
        ...(journeyId && journeyId !== sale.id
          ? [{ label: 'Jornada da venda', value: journeyId }]
          : []),
        { label: 'Produto', value: sale.productId },
        ...(sale.gatewayPaymentId
          ? [{ label: 'Pagamento no gateway', value: sale.gatewayPaymentId }]
          : []),
        ...(sale.lines ?? []).map((line, index) => ({
          label: `Linha ${index + 1}`,
          value: line.lineId,
        })),
        ...ledgerEntries.map((entry, index) => ({
          label: `Lançamento ${index + 1}`,
          value: entry.id,
        })),
      ]
    : []

  return (
    <Drawer
      data-testid="admin-sale-detail"
      open={saleId !== null}
      onClose={closeDrawer}
      title={sale?.buyerEmail ?? sale?.buyerName ?? 'Detalhe da cobrança'}
      description={
        sale
          ? `${adminProductLabel(sale)} · ${adminChargeLabel(sale)} · ${formatDateTime(sale.createdAt)}`
          : 'Cobrança, composição, movimentações e referências técnicas.'
      }
      size="audit"
      variant="financial"
      busy={refundMutation.isPending}
      status={
        salePresentation ? (
          <>
            <StatusBadge label={salePresentation.label} tone={salePresentation.tone} />
            {sale?.globalMarket ? <span className={saleChargeTag}>Mercado Global</span> : null}
            {sale ? <span className={saleChargeTag}>{adminChargeLabel(sale)}</span> : null}
            {sale?.handledByGlobalChargeback ? (
              <span className={saleChargeTag}>Chargeback global</span>
            ) : null}
          </>
        ) : undefined
      }
      tabs={
        sale ? (
          <DrawerTabs
            tabs={tabs}
            value={activeTab}
            onChange={setActiveTab}
            ariaLabel="Seções do detalhe da cobrança"
          />
        ) : undefined
      }
    >
      <div className={saleDetail}>
        {sale ? (
          <>
            <DrawerTabPanel id="summary" active={activeTab === 'summary'}>
              <DetailHero
                eyebrow="Valor da liquidação"
                value={
                  ledgerEntries.length > 0 ? (
                    <MoneyValue
                      amount={sale.grossAmount.amount}
                      currency={sale.grossAmount.currency}
                      size="lg"
                    />
                  ) : saleReceivables.isLoading ? (
                    'Conferindo ledger...'
                  ) : saleReceivables.isError ? (
                    'Ledger indisponível'
                  ) : (
                    'Sem liquidação'
                  )
                }
                aside={
                  <div>
                    <div>{buyerAmountLabel}</div>
                    <MoneyValue
                      amount={sale.amountPaid.amount}
                      currency={sale.amountPaid.currency}
                    />
                  </div>
                }
                description={
                  sale.status === 'FAILED'
                    ? 'A cobrança não foi concluída e não gerou liquidação.'
                    : sale.status === 'PENDING'
                      ? 'Aguardando confirmação do pagamento pelo gateway.'
                      : salePresentation?.label
                }
              />
              <Button variant="text" size="sm" onClick={() => setActiveTab('ledger')}>
                Ver extrato por participante
              </Button>
              <KeyValueGrid
                items={[
                  { label: 'ID da venda', value: sale.id },
                  {
                    label: 'Comprador',
                    value: sale.buyerEmail ?? sale.buyerName ?? 'Não informado',
                  },
                  {
                    label: 'Pagamento',
                    value: sale.paymentMethod
                      ? paymentMethodLabel(sale.paymentMethod)
                      : 'Não informado',
                  },
                  { label: 'Gateway', value: sale.gateway ?? 'Não informado' },
                  {
                    label: 'Produtor',
                    value: producerDisplay?.primary ?? 'E-mail não localizado',
                  },
                  ...(sale.coupon
                    ? [{ label: 'Cupom', value: couponSummaryLabel(sale.coupon) }]
                    : []),
                ]}
              />
              {sale.coupon ? <CouponDetails coupon={sale.coupon} /> : null}
              <div className={saleSection}>
                <span className={saleSectionTitle}>Reembolso</span>
                {!canRefund ? (
                  <span className={muted}>Seu perfil não possui a permissão SALE_REFUND.</span>
                ) : journey.isLoading ? (
                  <span className={muted}>Carregando cobranças da jornada...</span>
                ) : journey.isError ? (
                  <span className={muted}>
                    Não foi possível carregar todas as cobranças. Tente novamente antes de solicitar
                    o reembolso.
                  </span>
                ) : refundableTargets.length === 0 ? (
                  <span className={muted}>
                    Nenhuma cobrança desta jornada está disponível para reembolso manual.
                  </span>
                ) : (
                  <>
                    <span className={muted}>
                      {refundTargets.length > 1
                        ? `Esta jornada tem ${refundTargets.length} cobranças. Escolha qual deseja reembolsar.`
                        : 'Revise a cobrança e confirme o reembolso no próximo passo.'}
                    </span>
                    {refundTargets.length === 1 ? (
                      <Button
                        variant="danger"
                        size="sm"
                        disabled={refundMutation.isPending}
                        aria-label="Solicitar reembolso"
                        onClick={() => openRefund(refundableTargets[0]!)}
                      >
                        Solicitar reembolso
                      </Button>
                    ) : (
                      <div className={splitGrid} aria-label="Cobranças disponíveis para reembolso">
                        <span className={saleSectionTitle}>Reembolso por cobrança</span>
                        {refundTargets.map((target) => {
                          const targetPresentation = adminSaleStatusPresentation(target.sale)
                          return (
                            <div className={saleSplitRow} key={target.saleId}>
                              <div className={saleSplitLead}>
                                <span className={saleSplitName}>{target.label}</span>
                                <span className={saleSplitMeta}>{target.productName}</span>
                                {target.hasPartialRefund && target.canRequestRefund ? (
                                  <span className={saleSplitMeta}>
                                    Valor restante para reembolso
                                  </span>
                                ) : null}
                                {!target.canRequestRefund ? (
                                  <span className={saleSplitMeta}>{target.unavailableReason}</span>
                                ) : null}
                              </div>
                              <div className={saleItemAside}>
                                <StatusBadge
                                  label={targetPresentation.label}
                                  tone={targetPresentation.tone}
                                  size="sm"
                                />
                                <MoneyValue
                                  amount={target.refundAmount.amount}
                                  currency={target.refundAmount.currency}
                                  size="sm"
                                />
                                {target.canRequestRefund ? (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    disabled={refundMutation.isPending}
                                    aria-label={`Reembolsar ${target.label.toLocaleLowerCase('pt-BR')}`}
                                    onClick={() => openRefund(target)}
                                  >
                                    Reembolsar
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              <AdminSaleFinancialSummary
                sale={sale}
                ledgerEntries={ledgerEntries}
                ledgerLoading={saleReceivables.isLoading}
                ledgerError={saleReceivables.isError}
              />
            </DrawerTabPanel>

            <DrawerTabPanel id="composition" active={activeTab === 'composition'}>
              {sale.coupon ? <CouponDetails coupon={sale.coupon} /> : null}
              {(sale.lines?.length ?? 0) > 0 ? (
                <div className={saleSection}>
                  <span className={saleSectionTitle}>Composição da cobrança</span>
                  <div>
                    {sale.lines!.map((item) => (
                      <div className={saleSplitRow} key={item.lineId}>
                        <div className={saleSplitLead}>
                          <span className={saleSplitName}>{adminSaleLineProductLabel(item)}</span>
                          <span className={saleSplitMeta}>
                            {adminSaleLineKindLabel(item.kind)}
                            {adminSaleLineOfferLabel(item)
                              ? ` · ${adminSaleLineOfferLabel(item)}`
                              : ''}
                          </span>
                        </div>
                        <div className={saleItemAside}>
                          <span className={saleSplitMeta}>Valor da liquidação</span>
                          {item.settlementAmount ? (
                            <MoneyValue
                              amount={item.settlementAmount.amount}
                              currency={item.settlementAmount.currency}
                              size="sm"
                            />
                          ) : (
                            <span className={saleSplitMeta}>Não informada</span>
                          )}
                          <span className={saleSplitMeta}>{buyerAmountLabel}</span>
                          <MoneyValue
                            amount={item.amount.amount}
                            currency={item.amount.currency}
                            size="sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {sale.lines!.length > 1 &&
                  sale.financialEvents?.some((event) => event.kind === 'PARTIAL_REFUND') ? (
                    <span className={saleLineHint}>
                      O gateway informou valor parcial para a cobrança. Sem um identificador de
                      item, o financeiro distribui o impacto proporcionalmente entre estas linhas.
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div className={saleSection}>
                <span className={saleSectionTitle}>Taxas e gateway</span>
                <div className={saleLine}>
                  <span className={saleLineLabel}>
                    Serviço VendePay
                    {vendepayFeeBasis ? (
                      <span className={saleLineHint}>{vendepayFeeBasis}</span>
                    ) : null}
                  </span>
                  <span className={saleLineValue}>
                    <MoneySummaryList
                      rows={sumByCurrency([...vendepayFees, ...currencyFees])}
                      empty="Serviço não informado no ledger."
                    />
                  </span>
                </div>
                {vendepayFeeBreakdown.length > 0 ? (
                  <div aria-label="Composição da taxa Vendepay">
                    {vendepayFeeBreakdown.map((component) => (
                      <div className={saleSplitRow} key={component.key}>
                        <div className={saleSplitLead}>
                          <span className={saleSplitName}>{feeComponentLabel(component)}</span>
                          <span className={saleSplitMeta}>{feeComponentMeta(component)}</span>
                        </div>
                        <div className={saleItemAside}>
                          <MoneyValue
                            amount={component.amount.amount}
                            currency={component.amount.currency}
                            size="sm"
                          />
                        </div>
                      </div>
                    ))}
                    {sumByCurrency(vendepayFees).map((total) => (
                      <MoneyReconciliation
                        key={total.currency}
                        total={total}
                        parts={vendepayFeeBreakdown
                          .filter((component) => component.amount.currency === total.currency)
                          .map((component) => component.amount)}
                      />
                    ))}
                  </div>
                ) : null}
                {currencyFees.length > 0 ? (
                  <div className={saleLine}>
                    <span className={saleLineLabel}>
                      Conversão descontada do vendedor (histórico)
                    </span>
                    <span className={saleLineValue}>
                      <MoneySummaryList
                        rows={sumByCurrency(currencyFees)}
                        empty="Sem taxa de conversão no ledger."
                      />
                    </span>
                  </div>
                ) : null}
                {buyerConversion.length > 0 ? (
                  <div className={saleLine}>
                    <span className={saleLineLabel}>Conversão paga pelo comprador</span>
                    <span className={saleLineValue}>
                      <MoneySummaryList
                        rows={sumByCurrency(buyerConversion)}
                        empty="Sem conversão adicional."
                      />
                    </span>
                  </div>
                ) : null}
                {otherVendepayFees.length > 0 ? (
                  <div className={saleLine}>
                    <span className={saleLineLabel}>Outras taxas Vendepay</span>
                    <span className={saleLineValue}>
                      <MoneySummaryList
                        rows={sumByCurrency(otherVendepayFees)}
                        empty="Sem outras taxas."
                      />
                    </span>
                  </div>
                ) : null}
                <hr className={saleDivider} />
                <div className={salePlatformRow}>
                  <div className={saleNetLead}>
                    <span className={salePlatformLabel}>Serviço descontado da venda</span>
                    <span className={salePlatformNote}>
                      Mesmo valor de serviço apresentado ao vendedor. A conversão paga pelo
                      comprador e os custos do provedor ficam separados.
                    </span>
                  </div>
                  <span className={salePlatformValue}>
                    <MoneySummaryList
                      rows={sumByCurrency([...vendepayFees, ...currencyFees])}
                      empty="Sem receita Vendepay no ledger."
                    />
                  </span>
                </div>
                <div className={saleNetRow}>
                  <div className={saleNetLead}>
                    <span className={saleNetLabel}>Líquido aos participantes</span>
                    <span className={saleNetNote}>
                      Soma de produtor, afiliado, coprodutor e demais destinatários desta cobrança.
                    </span>
                  </div>
                  <span className={saleNetValue}>
                    <MoneySummaryList
                      rows={participantTotals}
                      empty="Sem repasse aos participantes."
                    />
                  </span>
                </div>
              </div>

              <div className={saleSection}>
                <span className={saleSectionTitle}>
                  Split da cobrança: quanto cada pessoa gerou
                </span>
                {split.length > 0 ? (
                  <div>
                    {split.map((part) => {
                      const identity = adminUserDisplay({
                        userId: part.recipientId,
                        usersById: participantLookup.usersById,
                        isLoading: participantLookup.isLoading,
                      })

                      return (
                        <div className={saleSplitRow} key={part.key}>
                          <div className={saleSplitLead}>
                            <span className={saleSplitName}>
                              {part.label}
                              <span className={saleChargeTag}>{part.currency}</span>
                            </span>
                            <span className={saleUserIdentity} title={identity.title}>
                              <span className={saleUserName}>{identity.primary}</span>
                              {identity.secondary ? (
                                <span className={saleSplitMeta}>{identity.secondary}</span>
                              ) : null}
                            </span>
                            <span className={saleSplitMeta}>{part.buckets.join(' · ')}</span>
                            <span className={saleStatusPills}>
                              {part.statuses.map((status) => (
                                <StatusBadge
                                  label={ledgerStatusLabel(status)}
                                  tone={statusTone(status)}
                                  size="sm"
                                  key={status}
                                />
                              ))}
                            </span>
                            <span className={saleSplitMeta}>
                              Lançado:{' '}
                              <MoneyValue
                                amount={part.payable}
                                currency={part.currency}
                                size="sm"
                              />{' '}
                              · Reserva:{' '}
                              <MoneyValue
                                amount={part.reserve}
                                currency={part.currency}
                                size="sm"
                              />
                            </span>
                            {Number(part.debtSettled) > 0 ? (
                              <span className={saleSplitMeta}>
                                Abateu dívida:{' '}
                                <MoneyValue
                                  amount={part.debtSettled}
                                  currency={part.currency}
                                  size="sm"
                                />
                              </span>
                            ) : null}
                            {Number(part.debtRemainder) > 0 ? (
                              <span className={saleSplitMeta}>
                                Remanescente após dívida:{' '}
                                <MoneyValue
                                  amount={part.debtRemainder}
                                  currency={part.currency}
                                  size="sm"
                                />
                              </span>
                            ) : null}
                            <MoneyReconciliation
                              total={{ amount: part.total, currency: part.currency }}
                              parts={[
                                { amount: part.payable, currency: part.currency },
                                { amount: part.reserve, currency: part.currency },
                              ]}
                            />
                          </div>
                          <div className={saleItemAside}>
                            <span className={saleDetailOffer}>Total gerado</span>
                            <MoneyValue amount={part.total} currency={part.currency} size="sm" />
                          </div>
                        </div>
                      )
                    })}
                    {participantTotals.map((total) => (
                      <MoneyReconciliation
                        key={total.currency}
                        total={total}
                        parts={split
                          .filter((part) => part.currency === total.currency)
                          .map((part) => ({ amount: part.total, currency: part.currency }))}
                      />
                    ))}
                    <div className={saleCompositionTotalRow}>
                      <span className={saleCompositionTotalLabel}>Total para participantes</span>
                      <span className={saleLineValue}>
                        <MoneySummaryList
                          rows={participantTotals}
                          empty="Sem repasse aos participantes."
                        />
                      </span>
                    </div>
                  </div>
                ) : saleReceivables.isLoading ? (
                  <span className={muted}>Carregando split...</span>
                ) : (
                  <span className={muted}>Sem split financeiro para esta cobrança.</span>
                )}
              </div>
              <AdminProviderChargeResult
                sale={sale}
                entries={ledgerEntries}
                loading={saleReceivables.isLoading}
                error={saleReceivables.isError}
              />
            </DrawerTabPanel>

            <DrawerTabPanel id="ledger" active={activeTab === 'ledger'}>
              <AdminProviderChargeResult
                sale={sale}
                entries={ledgerEntries}
                loading={saleReceivables.isLoading}
                error={saleReceivables.isError}
              />

              <div className={saleSection}>
                <span className={saleSectionTitle}>Extrato da cobrança</span>
                {ledgerGroups.length > 0 ? (
                  <div className={splitGrid}>
                    {ledgerGroups.map((group) => {
                      const groupIdentity = group.recipientId
                        ? adminUserDisplay({
                            userId: group.recipientId,
                            usersById: participantLookup.usersById,
                            isLoading: participantLookup.isLoading,
                          })
                        : null

                      return (
                        <div className={saleStatementGroup} key={group.key}>
                          <div className={saleStatementHeader}>
                            <div className={saleSplitLead}>
                              <span className={saleStatementTitle}>{group.label}</span>
                              {groupIdentity ? (
                                <span className={saleUserIdentity} title={groupIdentity.title}>
                                  <span className={saleUserName}>{groupIdentity.primary}</span>
                                  {groupIdentity.secondary ? (
                                    <span className={saleSplitMeta}>{groupIdentity.secondary}</span>
                                  ) : null}
                                </span>
                              ) : (
                                <span className={saleSplitMeta}>{group.subtitle}</span>
                              )}
                            </div>
                            <div className={saleItemAside}>
                              {group.recipientId ? (
                                <NavigationLink
                                  href={`/financas?userId=${encodeURIComponent(group.recipientId)}`}
                                >
                                  Ver extrato da pessoa
                                </NavigationLink>
                              ) : null}
                              <MoneySummaryList rows={group.totals} empty="R$ 0,00" />
                              <span className={saleDetailOffer}>
                                {group.entries.length}{' '}
                                {group.entries.length === 1 ? 'lançamento' : 'lançamentos'}
                              </span>
                            </div>
                          </div>
                          {group.totals.map((total) => (
                            <MoneyReconciliation
                              key={total.currency}
                              total={total}
                              parts={group.entries
                                .filter((entry) => entry.amount.currency === total.currency)
                                .map((entry) => entry.amount)}
                            />
                          ))}
                          <div className={saleStatementRows}>
                            {group.entries.map((entry) => {
                              const hideFeeSettlementMeta = isVendepaySaleFeeLedgerEntry(entry)
                              const entryIdentity = entry.recipientId
                                ? adminUserDisplay({
                                    userId: entry.recipientId,
                                    usersById: participantLookup.usersById,
                                    isLoading: participantLookup.isLoading,
                                  })
                                : null
                              const feeComponents = isVendepaySaleFeeLedgerEntry(entry)
                                ? vendepayFeeComponents([entry])
                                : []
                              const feeBreakdown = feeComponents.some(
                                (component) => component.type === 'FIXED',
                              )
                                ? feeComponents
                                    .map(
                                      (component) =>
                                        `${feeComponentLabel(component)} ${component.amount.currency} ${component.amount.amount}`,
                                    )
                                    .join(' · ')
                                : null
                              const entryMeta = [
                                entryIdentity ? `Destinatário ${entryIdentity.primary}` : null,
                                entry.appliedPercentage
                                  ? `${customServiceFees ? formatPercentageLabel(entry.appliedPercentage) : `${entry.appliedPercentage}%`} aplicado`
                                  : null,
                                entry.baseAmount
                                  ? `Base ${entry.baseAmount.currency} ${entry.baseAmount.amount}`
                                  : null,
                                feeBreakdown,
                                entry.availability?.cardCountry
                                  ? `País de emissão do cartão: ${entry.availability.cardCountry}`
                                  : null,
                                !hideFeeSettlementMeta
                                  ? entry.availability?.status === 'available'
                                    ? 'Disponibilidade confirmada na conta de pagamento. Não confirma depósito bancário.'
                                    : entry.availableAt
                                      ? `Liberação prevista: ${new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: customServiceFees ? 'America/Sao_Paulo' : 'UTC' }).format(new Date(entry.availableAt))}${customServiceFees ? ' (Brasília)' : ''}. Não é previsão bancária.`
                                      : 'Aguardando previsão de liberação'
                                  : null,
                              ].filter(Boolean)

                              return (
                                <div className={saleStatementEntry} key={entry.id}>
                                  <div className={saleEntryBody}>
                                    <span className={saleLineLabel}>
                                      {ledgerEntryDisplayLabel(entry)}
                                      <span className={saleLineHint}>
                                        {ledgerEntryDisplayHint(entry)}
                                      </span>
                                    </span>
                                    <span className={saleEntryAmount}>
                                      {!hideFeeSettlementMeta ? (
                                        <StatusBadge
                                          label={ledgerStatusLabel(entry.status)}
                                          tone={statusTone(entry.status)}
                                          size="sm"
                                        />
                                      ) : null}
                                      <MoneyValue
                                        amount={entry.amount.amount}
                                        currency={entry.amount.currency}
                                        size="sm"
                                      />
                                    </span>
                                  </div>
                                  <span className={saleEntryMeta}>{entryMeta.join(' · ')}</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : saleReceivables.isLoading ? (
                  <span className={muted}>Carregando extrato...</span>
                ) : (
                  <span className={muted}>Sem lançamentos no extrato desta cobrança.</span>
                )}
              </div>
            </DrawerTabPanel>

            <DrawerTabPanel id="technical" active={activeTab === 'technical'}>
              <TechnicalDetails rows={technicalRows} defaultOpen />
            </DrawerTabPanel>
          </>
        ) : detail.isLoading ? (
          <p className={muted}>Carregando cobrança...</p>
        ) : (
          <p className={muted}>Cobrança não encontrada.</p>
        )}
      </div>
      <AdminSaleRefundDialog
        target={refundTarget}
        buyer={sale?.buyerEmail ?? sale?.buyerName ?? 'Não informado'}
        journeyChargeCount={refundTargets.length}
        reason={refundReason}
        loading={refundMutation.isPending}
        onReasonChange={setRefundReason}
        onCancel={() => {
          if (!refundMutation.isPending) {
            setRefundTarget(null)
            setRefundReason('')
          }
        }}
        onConfirm={() => {
          if (refundTarget) refundMutation.mutate(refundTarget.saleId)
        }}
      />
    </Drawer>
  )
}
